import React, { useRef, useState, useEffect, useCallback } from 'react';
import { 
  Pencil, 
  Circle, 
  Square, 
  ArrowRight, 
  Highlighter,
  MapPin,
  Undo,
  Redo,
  Trash2,
  Save,
  ZoomIn,
  ZoomOut,
  RotateCcw
} from 'lucide-react';
import { FrameMarker, DrawingAction } from '../../types';

type ToolType = 'freehand' | 'arrow' | 'circle' | 'rectangle' | 'highlighter' | 'marker';

interface MarkupEditorProps {
  imageData: string;
  markers: FrameMarker[];
  onMarkersChange: (markers: FrameMarker[]) => void;
  onSave: (markupImageData: string) => void;
  onMarkerSelect?: (markerId: string | null) => void;
  selectedMarkerId?: string | null;
}

const COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#eab308', // yellow
  '#22c55e', // green
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#000000', // black
  '#ffffff', // white
];

const BRUSH_SIZES = [2, 4, 6, 8, 12];

export const MarkupEditor: React.FC<MarkupEditorProps> = ({
  imageData,
  markers,
  onMarkersChange,
  onSave,
  onMarkerSelect,
  selectedMarkerId
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [tool, setTool] = useState<ToolType>('freehand');
  const [color, setColor] = useState('#ef4444');
  const [brushSize, setBrushSize] = useState(4);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [currentPath, setCurrentPath] = useState<{ x: number; y: number }[]>([]);
  const [drawingActions, setDrawingActions] = useState<DrawingAction[]>([]);
  const [redoStack, setRedoStack] = useState<DrawingAction[]>([]);
  const [zoom, setZoom] = useState(1);
  const [baseImage, setBaseImage] = useState<HTMLImageElement | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });

  // Load base image
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setBaseImage(img);
      setCanvasSize({ width: img.width, height: img.height });
    };
    img.src = imageData;
  }, [imageData]);

  // Redraw canvas when anything changes
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !baseImage) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw base image
    ctx.drawImage(baseImage, 0, 0);

    // Draw all saved actions
    drawingActions.forEach(action => {
      drawAction(ctx, action);
    });

    // Draw current path (while drawing)
    if (currentPath.length > 0 && isDrawing) {
      const tempAction: DrawingAction = {
        type: tool,
        points: currentPath,
        color,
        lineWidth: brushSize
      };
      drawAction(ctx, tempAction);
    }

    // Draw markers
    markers.forEach((marker, index) => {
      drawMarker(ctx, marker, index + 1, marker.id === selectedMarkerId);
    });
  }, [baseImage, drawingActions, currentPath, isDrawing, tool, color, brushSize, markers, selectedMarkerId]);

  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Draw a single action
  const drawAction = (ctx: CanvasRenderingContext2D, action: DrawingAction) => {
    ctx.strokeStyle = action.color;
    ctx.fillStyle = action.color;
    ctx.lineWidth = action.lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    switch (action.type) {
      case 'freehand':
        if (action.points.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(action.points[0].x, action.points[0].y);
        action.points.forEach((point, i) => {
          if (i > 0) ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
        break;

      case 'highlighter':
        if (action.points.length < 2) return;
        ctx.globalAlpha = 0.3;
        ctx.lineWidth = action.lineWidth * 4;
        ctx.beginPath();
        ctx.moveTo(action.points[0].x, action.points[0].y);
        action.points.forEach((point, i) => {
          if (i > 0) ctx.lineTo(point.x, point.y);
        });
        ctx.stroke();
        ctx.globalAlpha = 1;
        break;

      case 'arrow':
        if (action.points.length < 2) return;
        const start = action.points[0];
        const end = action.points[action.points.length - 1];
        
        // Draw line
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
        
        // Draw arrowhead
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const headLength = 15 + action.lineWidth;
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(
          end.x - headLength * Math.cos(angle - Math.PI / 6),
          end.y - headLength * Math.sin(angle - Math.PI / 6)
        );
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(
          end.x - headLength * Math.cos(angle + Math.PI / 6),
          end.y - headLength * Math.sin(angle + Math.PI / 6)
        );
        ctx.stroke();
        break;

      case 'circle':
        if (action.points.length < 2) return;
        const circleStart = action.points[0];
        const circleEnd = action.points[action.points.length - 1];
        const radius = Math.sqrt(
          Math.pow(circleEnd.x - circleStart.x, 2) + 
          Math.pow(circleEnd.y - circleStart.y, 2)
        );
        ctx.beginPath();
        ctx.arc(circleStart.x, circleStart.y, radius, 0, 2 * Math.PI);
        ctx.stroke();
        break;

      case 'rectangle':
        if (action.points.length < 2) return;
        const rectStart = action.points[0];
        const rectEnd = action.points[action.points.length - 1];
        ctx.strokeRect(
          rectStart.x,
          rectStart.y,
          rectEnd.x - rectStart.x,
          rectEnd.y - rectStart.y
        );
        break;
    }
  };

  // Draw a marker
  const drawMarker = (ctx: CanvasRenderingContext2D, marker: FrameMarker, number: number, isSelected: boolean) => {
    const size = isSelected ? 32 : 28;
    
    // Draw pin shape
    ctx.beginPath();
    ctx.fillStyle = isSelected ? '#f97316' : '#ef4444';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    
    // Circle part
    ctx.arc(marker.x, marker.y - size/2, size/2, 0, 2 * Math.PI);
    ctx.fill();
    ctx.stroke();
    
    // Number
    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${size * 0.5}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(number.toString(), marker.x, marker.y - size/2);
    
    // Point at bottom
    ctx.beginPath();
    ctx.fillStyle = isSelected ? '#f97316' : '#ef4444';
    ctx.moveTo(marker.x - size/4, marker.y - size/4);
    ctx.lineTo(marker.x, marker.y + 4);
    ctx.lineTo(marker.x + size/4, marker.y - size/4);
    ctx.fill();
  };

  // Get canvas coordinates from mouse event
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e);

    if (tool === 'marker') {
      // Check if clicking on existing marker
      const clickedMarker = markers.find(m => {
        const dist = Math.sqrt(Math.pow(m.x - coords.x, 2) + Math.pow(m.y - coords.y, 2));
        return dist < 20;
      });

      if (clickedMarker) {
        onMarkerSelect?.(clickedMarker.id);
      } else {
        // Add new marker
        const newMarker: FrameMarker = {
          id: `marker-${Date.now()}`,
          x: coords.x,
          y: coords.y,
          note: ''
        };
        onMarkersChange([...markers, newMarker]);
        onMarkerSelect?.(newMarker.id);
      }
      return;
    }

    setIsDrawing(true);
    setStartPoint(coords);
    setCurrentPath([coords]);
    setRedoStack([]); // Clear redo stack on new action
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const coords = getCanvasCoordinates(e);

    if (tool === 'freehand' || tool === 'highlighter') {
      setCurrentPath(prev => [...prev, coords]);
    } else {
      // For shapes, only keep start and current point
      setCurrentPath([startPoint!, coords]);
    }
  };

  const handleMouseUp = () => {
    if (!isDrawing || currentPath.length < 2) {
      setIsDrawing(false);
      setCurrentPath([]);
      return;
    }

    // Save the action
    const newAction: DrawingAction = {
      type: tool,
      points: [...currentPath],
      color,
      lineWidth: brushSize
    };

    setDrawingActions(prev => [...prev, newAction]);
    setIsDrawing(false);
    setCurrentPath([]);
    setStartPoint(null);
  };

  // Undo/Redo
  const handleUndo = () => {
    if (drawingActions.length === 0) return;
    
    const lastAction = drawingActions[drawingActions.length - 1];
    setDrawingActions(prev => prev.slice(0, -1));
    setRedoStack(prev => [...prev, lastAction]);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    
    const nextAction = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setDrawingActions(prev => [...prev, nextAction]);
  };

  // Clear all
  const handleClear = () => {
    if (confirm('Clear all markups? This cannot be undone.')) {
      setDrawingActions([]);
      setRedoStack([]);
    }
  };

  // Save markup
  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const markupData = canvas.toDataURL('image/png');
    onSave(markupData);
  };

  // Zoom controls
  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
  const handleResetZoom = () => setZoom(1);

  // Delete selected marker
  const handleDeleteSelectedMarker = () => {
    if (!selectedMarkerId) return;
    onMarkersChange(markers.filter(m => m.id !== selectedMarkerId));
    onMarkerSelect?.(null);
  };

  const tools: { type: ToolType; icon: React.ReactNode; label: string }[] = [
    { type: 'freehand', icon: <Pencil className="w-5 h-5" />, label: 'Freehand' },
    { type: 'arrow', icon: <ArrowRight className="w-5 h-5" />, label: 'Arrow' },
    { type: 'circle', icon: <Circle className="w-5 h-5" />, label: 'Circle' },
    { type: 'rectangle', icon: <Square className="w-5 h-5" />, label: 'Rectangle' },
    { type: 'highlighter', icon: <Highlighter className="w-5 h-5" />, label: 'Highlighter' },
    { type: 'marker', icon: <MapPin className="w-5 h-5" />, label: 'Add Marker' },
  ];

  return (
    <div className="flex flex-col h-full bg-gray-100 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between p-2 bg-white border-b border-gray-200">
        {/* Drawing Tools */}
        <div className="flex items-center space-x-1">
          {tools.map(({ type, icon, label }) => (
            <button
              key={type}
              onClick={() => setTool(type)}
              className={`p-2 rounded-lg transition-colors ${
                tool === type 
                  ? 'bg-orange-100 text-orange-600' 
                  : 'hover:bg-gray-100 text-gray-600'
              }`}
              title={label}
            >
              {icon}
            </button>
          ))}

          <div className="w-px h-6 bg-gray-300 mx-2" />

          {/* Color Picker */}
          <div className="flex items-center space-x-1">
            {COLORS.map(c => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-6 h-6 rounded-full border-2 transition-transform ${
                  color === c ? 'border-gray-800 scale-110' : 'border-gray-300'
                }`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2" />

          {/* Brush Size */}
          <div className="flex items-center space-x-1">
            {BRUSH_SIZES.map(size => (
              <button
                key={size}
                onClick={() => setBrushSize(size)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                  brushSize === size 
                    ? 'bg-orange-100 text-orange-600' 
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
                title={`${size}px`}
              >
                <div 
                  className="rounded-full bg-current"
                  style={{ width: size + 2, height: size + 2 }}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-1">
          <button
            onClick={handleUndo}
            disabled={drawingActions.length === 0}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Undo"
          >
            <Undo className="w-5 h-5" />
          </button>
          <button
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Redo"
          >
            <Redo className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-300 mx-2" />

          <button
            onClick={handleZoomOut}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            title="Zoom Out"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-600 w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button
            onClick={handleZoomIn}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            title="Zoom In"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <button
            onClick={handleResetZoom}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
            title="Reset Zoom"
          >
            <RotateCcw className="w-5 h-5" />
          </button>

          <div className="w-px h-6 bg-gray-300 mx-2" />

          {selectedMarkerId && (
            <button
              onClick={handleDeleteSelectedMarker}
              className="p-2 rounded-lg hover:bg-red-100 text-red-600"
              title="Delete Selected Marker"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}

          <button
            onClick={handleClear}
            className="p-2 rounded-lg hover:bg-red-100 text-red-600"
            title="Clear All"
          >
            <Trash2 className="w-5 h-5" />
          </button>

          <button
            onClick={handleSave}
            className="flex items-center px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            title="Save Markup"
          >
            <Save className="w-4 h-4 mr-2" />
            Save
          </button>
        </div>
      </div>

      {/* Canvas Container */}
      <div 
        ref={containerRef}
        className="flex-1 overflow-auto flex items-center justify-center p-4"
      >
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          className="border border-gray-300 rounded-lg shadow-lg cursor-crosshair"
          style={{
            transform: `scale(${zoom})`,
            transformOrigin: 'center center',
            maxWidth: '100%',
            maxHeight: '100%'
          }}
        />
      </div>

      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-t border-gray-200 text-sm text-gray-600">
        <span>Tool: {tools.find(t => t.type === tool)?.label}</span>
        <span>Markers: {markers.length}</span>
        <span>Actions: {drawingActions.length}</span>
      </div>
    </div>
  );
};
